/**
 * features/communications/ParentContacts.tsx
 * ------------------------------------------------------------------
 * MISSING PARENT EMAILS.
 *
 * 156 families have never given LKC an email address, so they receive no
 * reports, no invoices and no notices. Nothing in the portal will produce
 * one; the person who can ask is the class teacher, who sees the child every
 * day. This is where they write down the answer.
 *
 * ONE SCREEN, TWO JOBS, decided by who is looking:
 *
 *   a class teacher sees their own class and nothing else, and can propose
 *     an address for any pupil on it;
 *   the office sees every class through a picker, and additionally gets the
 *     queue of submissions waiting to be approved.
 *
 * Nothing a teacher types takes effect on its own. It is stored PENDING and
 * the office applies it — see lib/contactSubmission for why, and for the rule
 * that a teacher may FILL A GAP but never REDIRECT A FAMILY. The narrowing
 * here is a convenience; the control is the callable, which checks all of it
 * again.
 * ------------------------------------------------------------------ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  MailPlus, Check, X, Phone, RefreshCw, ShieldCheck, Inbox, AlertTriangle,
} from 'lucide-react';
import { functions } from '@/lib/firebase';
import { Spinner } from '@/components/ui/primitives';
import {
  SoftCard, PanelHeader, StatChip, AccentButton, AccentPanel, glassFieldClass,
} from '@/features/finance/ui';
import { submissionProblem, type SubmissionStatus } from '@/lib/contactSubmission';
import { dedupePhones } from '@/lib/missingContacts';

interface GapRow {
  studentId: string;
  studentName: string;
  admissionNumber: string;
  classId: string;
  className: string;
  guardianId: string;
  guardianName: string;
  phones: string[];
  heldEmail: string;
}
interface PendingRow {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  email: string;
  guardianName?: string | null;
  previousEmail?: string | null;
  status: SubmissionStatus;
  submittedByName?: string;
  submittedAt: string;
}
interface GapsDto {
  checkedAt: string;
  isApprover: boolean;
  classes: { id: string; name: string }[];
  gaps: GapRow[];
  pending: PendingRow[];
}

export default function ParentContacts() {
  const [data, setData] = useState<GapsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [classId, setClassId] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async (cid?: string) => {
    setLoading(true);
    setErr('');
    try {
      const fn = httpsCallable(functions, 'classContactGaps');
      const res = await fn({ classId: cid ?? '' });
      setData(res.data as GapsDto);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /* A pupil already waiting on approval is not offered again — the teacher
     has done their part and showing the row a second time reads as if the
     first attempt was lost. */
  const waiting = useMemo(
    () => new Set((data?.pending ?? []).filter((p) => p.status === 'pending').map((p) => p.studentId)),
    [data],
  );
  const rows = useMemo(
    () => (data?.gaps ?? []).filter((g) => !waiting.has(g.studentId)),
    [data, waiting],
  );

  async function submit(row: GapRow) {
    const email = (drafts[row.studentId] ?? '').trim();
    const problem = submissionProblem(
      { studentId: row.studentId, email },
      { workingEmail: null, pendingEmails: [] },
    );
    if (problem) { setNote(problem); return; }
    setBusy(row.studentId);
    setNote('');
    try {
      const fn = httpsCallable(functions, 'submitParentContact');
      await fn({
        studentId: row.studentId,
        email,
        guardianId: row.guardianId || undefined,
        guardianName: row.guardianName || undefined,
      });
      setDrafts((d) => ({ ...d, [row.studentId]: '' }));
      setNote(`Sent to the office for approval — ${row.studentName}.`);
      await load(classId);
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function review(p: PendingRow, action: 'approve' | 'reject') {
    setBusy(p.id);
    setNote('');
    try {
      const fn = httpsCallable(functions, 'reviewParentContact');
      await fn({ submissionId: p.id, action });
      setNote(action === 'approve'
        ? `Applied — ${p.studentName} can now be reached at ${p.email}.`
        : `Rejected — ${p.studentName}.`);
      await load(classId);
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (loading && !data) return <div className="p-6 flex justify-center"><Spinner label="Loading…" /></div>;

  if (err) {
    return (
      <SoftCard>
        <p className="text-sm text-red-600 dark:text-red-400">{err}</p>
      </SoftCard>
    );
  }

  const pending = (data?.pending ?? []).filter((p) => p.status === 'pending');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatChip tone={rows.length ? 'red' : 'emerald'} icon={MailPlus}
          value={String(rows.length)} label="Parents we cannot reach" />
        <StatChip tone="amber" icon={Inbox}
          value={String(pending.length)} label="Waiting for the office" />
        {data?.isApprover && (
          <StatChip tone="slate" icon={ShieldCheck}
            value={String(data.classes.length)} label="Classes you can see" />
        )}
      </div>

      {note && (
        <AccentPanel tone="navy">
          <p className="text-sm text-gray-700 dark:text-gray-200">{note}</p>
        </AccentPanel>
      )}

      {/* ---- the office's approval queue ------------------------- */}
      {data?.isApprover && pending.length > 0 && (
        <SoftCard pad="p-0">
          <PanelHeader
            icon={Inbox} tone="gold" title="Waiting for approval"
            subtitle="A class teacher collected these. Nothing has been written to the pupil's record yet."
          />
          <div className="p-4 space-y-2">
            {pending.map((p) => (
              <div key={p.id}
                className="rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 border-l-amber-500 px-3 py-2.5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {p.studentName}
                      <span className="ml-2 text-xs font-normal text-gray-400">{p.className}</span>
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                      <span className="font-medium">{p.email}</span>
                      {p.guardianName ? ` · ${p.guardianName}` : ''}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      from {p.submittedByName || 'a class teacher'} · {p.submittedAt.slice(0, 10)}
                      {p.previousEmail ? ` · replaces ${p.previousEmail}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <AccentButton icon={Check} onClick={() => void review(p, 'approve')} disabled={busy === p.id}>
                      Approve
                    </AccentButton>
                    <AccentButton icon={X} variant="soft" onClick={() => void review(p, 'reject')} disabled={busy === p.id}>
                      Reject
                    </AccentButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SoftCard>
      )}

      {/* ---- the pupils still to chase --------------------------- */}
      <SoftCard pad="p-0">
        <PanelHeader
          icon={MailPlus} tone="navy" title="Parents the school cannot reach"
          subtitle="No email address on file, so they get no reports, no invoices and no notices. Ask the parent, then enter it here."
          right={(
            <div className="flex items-center gap-2">
              {data?.isApprover && data.classes.length > 1 && (
                <select
                  className={glassFieldClass('text-sm')}
                  value={classId}
                  onChange={(e) => { setClassId(e.target.value); void load(e.target.value); }}
                >
                  <option value="">All classes</option>
                  {data.classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <AccentButton icon={RefreshCw} onClick={() => void load(classId)} disabled={loading}>
                {loading ? 'Checking…' : 'Refresh'}
              </AccentButton>
            </div>
          )}
        />

        <div className="p-4">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {pending.length > 0
                ? 'Everything here has been sent to the office and is waiting for approval.'
                : 'Every parent in your class can be reached. Nothing to do.'}
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((g) => {
                const phones = dedupePhones(g.phones);
                return (
                  <div key={g.studentId}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 border-l-[#2d5295] px-3 py-2.5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {g.studentName}
                          <span className="ml-2 text-xs font-normal text-gray-400">
                            {g.className}{g.admissionNumber ? ` · ${g.admissionNumber}` : ''}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {g.guardianName || 'No guardian recorded'}
                          {phones.length ? (
                            <span className="ml-2 inline-flex items-center gap-1 text-gray-600 dark:text-gray-300">
                              <Phone size={11} />{phones.join(' / ')}
                            </span>
                          ) : (
                            <span className="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              <AlertTriangle size={11} />no phone number either
                            </span>
                          )}
                        </p>
                        {/* The address on file is a fiction, not a typo: the
                            import made it from the pupil's surname. Saying so
                            stops a teacher trying to correct it. */}
                        {g.heldEmail && (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            On file (does not work): {g.heldEmail}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input
                          id={`email-${g.studentId}`}
                          type="email"
                          inputMode="email"
                          autoComplete="off"
                          placeholder="parent@example.com"
                          className={glassFieldClass('text-sm w-56')}
                          value={drafts[g.studentId] ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [g.studentId]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') void submit(g); }}
                        />
                        <AccentButton
                          icon={Check}
                          onClick={() => void submit(g)}
                          disabled={busy === g.studentId || !(drafts[g.studentId] ?? '').trim()}
                        >
                          {busy === g.studentId ? 'Sending…' : 'Send'}
                        </AccentButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {rows.length > 0 && (
            <p className="text-[11px] text-gray-400 mt-3">
              What you enter goes to the office for approval — it is not applied straight away.
            </p>
          )}
        </div>
      </SoftCard>
    </div>
  );
}
